import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  canReplyToNotification,
  getNotificationSenderTitle,
  getNotificationThread,
  isSystemNotification,
  replyToNotification,
  sortNotificationThread,
} from '../services/notificationService';

const accentStyles = {
  teal: {
    own: 'bg-[#E6F8F7] text-slate-800',
    button: 'bg-[#00C0B9] text-white disabled:cursor-not-allowed disabled:opacity-60',
    focus: 'focus:border-[#00C0B9]',
  },
  green: {
    own: 'bg-[#E9F9EE] text-slate-800',
    button: 'bg-[#1FC24E] text-white disabled:cursor-not-allowed disabled:opacity-60',
    focus: 'focus:border-[#1FC24E]',
  },
};

function formatNotificationDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'long',
  }).format(date);
}

function NotificationThreadSection({ notification, currentUserId, accent = 'teal' }) {
  const [thread, setThread] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const styles = accentStyles[accent] || accentStyles.teal;
  const canReply = canReplyToNotification(notification, currentUserId);
  const isSystem = isSystemNotification(notification);

  const loadThread = useCallback(async () => {
    if (!notification?.id) {
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const items = await getNotificationThread(notification.id);
      setThread(items.length > 0 ? items : [notification]);
    } catch {
      setThread([notification]);
    } finally {
      setIsLoading(false);
    }
  }, [notification]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  const messages = useMemo(() => sortNotificationThread(thread), [thread]);

  async function handleSubmit(event) {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!description.trim()) {
      setErrorMessage('متن پاسخ را وارد کنید.');
      return;
    }

    setIsSending(true);

    try {
      const replied = await replyToNotification(notification.id, {
        title,
        description,
      });
      setTitle('');
      setDescription('');
      setSuccessMessage('پاسخ شما ارسال شد.');

      if (replied) {
        setThread((prev) =>
          sortNotificationThread([
            ...prev.filter((item) => item.id !== replied.id),
            replied,
          ])
        );
      }

      loadThread();
    } catch (error) {
      setErrorMessage(error.message || 'امکان ارسال پاسخ وجود ندارد.');
    } finally {
      setIsSending(false);
    }
  }

  if (!notification) {
    return null;
  }

  return (
    <section className="space-y-3">
      <div>
        <p className="text-[14px] font-medium text-slate-800">گفتگو</p>
        <p className="mt-1 text-[12px] text-slate-500">
          پاسخ به طرف مقابل همین گفتگو ارسال می‌شود.
        </p>
      </div>

      {isLoading ? (
        <div className="rounded-[12px] bg-[#F8F8F8] px-4 py-6 text-center text-[12px] text-slate-500">
          در حال دریافت گفتگو...
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {messages.map((item) => {
            const isOwn = Boolean(currentUserId) && item.senderId === currentUserId;

            return (
              <article
                key={item.id}
                className={`max-w-[92%] rounded-[12px] px-3.5 py-3 ${
                  isOwn ? `mr-auto ${styles.own}` : 'ml-auto bg-[#F8F8F8] text-slate-800'
                }`}
              >
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
                  <span>{getNotificationSenderTitle(item, { currentUserId })}</span>
                  <span aria-hidden="true">·</span>
                  <time dateTime={item.createdAt}>{formatNotificationDate(item.createdAt)}</time>
                </div>

                {item.title ? (
                  <p className="mt-1.5 text-[13px] font-medium leading-6">{item.title}</p>
                ) : null}

                <p className="mt-1 whitespace-pre-line text-[13px] leading-7 text-slate-700">
                  {item.description || 'متنی برای این پیام ثبت نشده است.'}
                </p>
              </article>
            );
          })}
        </div>
      )}

      {isSystem ? (
        <p className="rounded-[12px] bg-slate-50 px-4 py-3 text-[12px] text-slate-500">
          اعلان‌های سیستمی قابل پاسخ نیستند.
        </p>
      ) : canReply ? (
        <form className="space-y-3 rounded-[12px] border border-slate-100 bg-white p-3.5" onSubmit={handleSubmit}>
          <label className="block text-[12px] text-slate-700">
            عنوان پاسخ
            <input
              className={`mt-2 w-full rounded-[12px] border border-slate-200 bg-[#F8F8F8] px-4 py-3 text-[13px] outline-none transition ${styles.focus} focus:bg-white`}
              type="text"
              placeholder="اختیاری"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>

          <label className="block text-[12px] text-slate-700">
            متن پاسخ
            <textarea
              className={`mt-2 min-h-[96px] w-full resize-y rounded-[12px] border border-slate-200 bg-[#F8F8F8] px-4 py-3 text-[13px] outline-none transition ${styles.focus} focus:bg-white`}
              placeholder="پاسخ خود را بنویسید"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              required
            />
          </label>

          <button
            type="submit"
            className={`w-full rounded-[8px] py-2.5 text-[13px] font-medium ${styles.button}`}
            disabled={isSending}
          >
            {isSending ? 'در حال ارسال...' : 'ارسال پاسخ'}
          </button>
        </form>
      ) : (
        <p className="rounded-[12px] bg-slate-50 px-4 py-3 text-[12px] text-slate-500">
          برای اعلان‌های گروهی نمی‌توان پاسخ ارسال کرد.
        </p>
      )}

      {errorMessage ? (
        <p className="rounded-[12px] bg-red-50 px-4 py-3 text-[12px] text-red-600">{errorMessage}</p>
      ) : null}

      {successMessage ? (
        <p className="rounded-[12px] bg-green-50 px-4 py-3 text-[12px] text-[#1FC24E]">{successMessage}</p>
      ) : null}
    </section>
  );
}

export default NotificationThreadSection;
